import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getStatusRegister,
  useStatusRegisterReducer
} from '@/main/store/statusRegister';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';

import { KeyValue } from '@/main/models/common';
import { BodyShopFilterRequest } from '@/main/models/request/bodyshopRequest';
import {
  getBodyshops,
  resetReducerBodyshops,
  useBodyshopReducer
} from '@/main/store/bodyshop';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { useBodyshopRegisterContext } from '../context';
import * as S from './styles';
import { getDefaultOptionDrop, getTranslateByKey } from '@/main/services/utils';
import {
  getBodyshopType,
  useBodyshopTypeReducer
} from '@/main/store/bodyshopType';
import { getEntityType, useEntityTypeReducer } from '@/main/store/entityType';

function SectionInfo() {
  const dispatch = useDispatch();
  const countryReducer = useCountryReducer();
  const bodyshopReducer = useBodyshopReducer();
  const statusRegisterReducer = useStatusRegisterReducer();
  const bodyshopTypeReducer = useBodyshopTypeReducer();
  const entityTypeReducer = useEntityTypeReducer();
  const {
    control,
    setValue,
    formState: { errors }
  } = useFormContext();
  const {
    hasScreenBranch,
    isAddBranch,
    typeCompany,
    isEdit
  } = useBodyshopRegisterContext();
  const optionDefaultCountryDrop = getDefaultOptionDrop(
    countryReducer.countriesUser.data
  );
  const optionDefaultBodyshopDrop = getDefaultOptionDrop(
    bodyshopReducer.bodyshops.data
  );

  const isDisabledCountry = () => isEdit || optionDefaultCountryDrop !== null;

  function callBodyshopApiDomain(optionSelected: KeyValue | null) {
    // Clean fields & reducers
    setValue('company', null);
    dispatch(resetReducerBodyshops());

    if (optionSelected !== null) {
      const request: BodyShopFilterRequest = {
        guidCountry: String(optionSelected.key),
        type: HeadQuarterBranchFilterEnum.Only_Headquarters,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getBodyshops(request));
    }
  }

  function getErrorsFieldRfc() {
    const errorTypeRfc = errors.rfc?.type;
    if (errorTypeRfc) {
      if (errorTypeRfc === 'minLength') {
        return getTranslateByKey(
          'information.rfcMustHaveAtLeast12Digits',
          true
        );
      }
      if (errorTypeRfc === 'required') {
        return getTranslateByKey('information.rfcRequired', true);
      }
    }

    return '';
  }

  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(getStatusRegister());
    dispatch(getBodyshopType());
    dispatch(getEntityType());
  }, []);

  useEffect(() => {
    // Call API's related by default country
    if (optionDefaultCountryDrop) {
      callBodyshopApiDomain(optionDefaultCountryDrop);
    }
  }, [optionDefaultCountryDrop]);

  return (
    <S.Container>
      <KogAutoCompleteControlled
        nameCustom="country"
        label={getTranslateByKey('information.country')}
        control={control}
        disabled={(hasScreenBranch && isAddBranch) || isDisabledCountry()}
        rulesCustom={{ required: true }}
        errorMessageCustom={
          errors.country?.type === 'required'
            ? getTranslateByKey('information.countryRequired', true)
            : ''
        }
        hasErrorCustom={!!errors.country}
        options={countryReducer.countriesUser.data}
        isLoading={countryReducer.countriesUser.isLoading}
        onChangeCustom={(optionSelected: KeyValue | null) => {
          callBodyshopApiDomain(optionSelected);
        }}
        id="id-registerBodyshop-txt-country"
        autoHighlightCustom={!!optionDefaultCountryDrop}
        setValueCustom={setValue}
      />
      {typeCompany === 'branch' ? (
        <KogAutoCompleteControlled
          required
          nameCustom="companyNameHeadquarter"
          label={getTranslateByKey('information.companyName')}
          control={control}
          disabled={hasScreenBranch && (isAddBranch || isEdit)}
          rulesCustom={{ required: true }}
          errorMessageCustom={
            errors.companyNameHeadquarter?.type === 'required'
              ? getTranslateByKey('information.companyNameRequired', true)
              : ''
          }
          hasErrorCustom={!!errors.companyNameHeadquarter}
          options={bodyshopReducer.bodyshops.data}
          isLoading={bodyshopReducer.bodyshops.isLoading}
          onChangeCustom={() => {}}
          id="id-registerBodyshop-txt-companyNameHeadquarter"
          autoHighlightCustom={!!optionDefaultBodyshopDrop}
          setValueCustom={setValue}
        />
      ) : (
        <KogInputTextControlledNew
          name="companyName"
          label={getTranslateByKey('information.companyName')}
          control={control}
          controllerProps={{ rules: { required: true } }}
          id="id-registerBodyshop-txt-companyName"
          errorMessageCustom={
            errors.companyName?.type === 'required'
              ? getTranslateByKey('information.companyNameRequired', true)
              : ''
          }
        />
      )}
      <KogInputTextControlledNew
        name="rfc"
        label={getTranslateByKey('information.rfc')}
        control={control}
        inputProps={{ maxLength: 20 }}
        controllerProps={{ rules: { required: true, minLength: 12 } }}
        helperText={getErrorsFieldRfc()}
        error={!!errors.rfc}
        id="id-registerBodyshop-txt-rfc"
      />
      <KogAutoCompleteControlled
        label={getTranslateByKey('information.bodyshopType')}
        nameCustom="bodyshopType"
        setValueCustom={setValue}
        options={bodyshopTypeReducer.bodyshopType.data}
        isLoading={bodyshopTypeReducer.bodyshopType.isLoading}
        control={control}
        rulesCustom={{ required: true }}
        errorMessageCustom={
          errors.status?.type === 'required'
            ? getTranslateByKey('information.bodyshopTypeRequired', true)
            : ''
        }
        hasErrorCustom={!!errors.status}
        onChangeCustom={() => {}}
        id="id-registerBodyshop-txt-bodyshopType"
      />

      {typeCompany === 'branch' && (
        <KogInputTextControlledNew
          name="companyName"
          label={getTranslateByKey('information.companyNameBranch')}
          control={control}
          controllerProps={{ rules: { required: true } }}
          id="id-registerBodyshop-txt-companyNameBranch"
          errorMessageCustom={
            errors.companyName?.type === 'required'
              ? getTranslateByKey('information.companyNameBranchRequired', true)
              : ''
          }
        />
      )}
      <KogInputTextControlledNew
        name="tradeName"
        label={getTranslateByKey('information.tradeName')}
        control={control}
        controllerProps={{ rules: { required: true } }}
        id="id-registerBodyshop-txt-tradeName"
        errorMessageCustom={
          errors.tradeName?.type === 'required'
            ? getTranslateByKey('information.tradeNameRequired', true)
            : ''
        }
      />
      <KogAutoCompleteControlled
        label={getTranslateByKey('information.entityType')}
        nameCustom="entityType"
        setValueCustom={setValue}
        options={entityTypeReducer.entityType.data}
        isLoading={entityTypeReducer.entityType.isLoading}
        control={control}
        rulesCustom={{ required: false }}
        hasErrorCustom={!!errors.status}
        onChangeCustom={() => {}}
        id="id-registerBodyshop-txt-entityType"
      />

      <KogAutoCompleteControlled
        label={getTranslateByKey('information.status')}
        nameCustom="status"
        valueDefaultKey={
          !isEdit ? String(StatusRegisterEnum.Active) : undefined
        }
        setValueCustom={setValue}
        options={statusRegisterReducer.statusRegister.data}
        isLoading={statusRegisterReducer.statusRegister.isLoading}
        control={control}
        rulesCustom={{ required: true }}
        errorMessageCustom={
          errors.status?.type === 'required'
            ? getTranslateByKey('information.statusRequired', true)
            : ''
        }
        hasErrorCustom={!!errors.status}
        onChangeCustom={() => {}}
        id="id-registerBodyshop-txt-status"
      />

      <KogInputTextControlledNew
        name="observation"
        label={getTranslateByKey('information.observation')}
        control={control}
        id="id-registerBodyshop-txt-observation"
      />
    </S.Container>
  );
}

export default SectionInfo;
